import { AnimationDuration } from "../constants/animation_duration.js";
import { createFragment } from "../utilities/elements.js";
import { prefersReducedMotion } from "../utilities/media.js";
import { defineElement } from "./registry.js";
import { DickTokStandaloneComponent } from "./StandaloneComponent.js";

const CssVariables = Object.freeze({
  FONT_SIZE: "--dt-expandabletext__font-size",
  LINE_HEIGHT: "--dt-expandabletext__line-height",
  LINES: "--dt-expandabletext__lines",
  MAX_HEIGHT: "--dt-expandabletext__max-height",
});

/**
 * UI component to clamp the height of a text block to a fixed number of lines.
 * On click, the text block animates to the full height of the contents.
 *
 * The `lines` attribute configures the number of lines to clamp, default is 3.
 *
 * The `controlled` attribute prevents the default toggle behavior. The element
 * can also be toggled in JavaScript using the `open()` and `close()` methods.
 *
 * Text styling (font-size and line-height) is inherited from the parent, although
 * if the line-height is "normal", it will be clamped to 1.4. The element children
 * must be only text nodes or inline elements (for example, `span`, `a`).
 */
export class DickTokExpandableText extends DickTokStandaloneComponent {
  static attributes = [
    { name: "lines", type: Number, optional: true },
    { name: "controlled", type: Boolean },
  ];

  static targets = ["container", "content"];

  static template = createFragment`
    <div
        class="dt-expandabletext__container"
        data-target="container"
        data-action="click:dt-expandabletext->toggle"
    >
        <div class="dt-expandabletext__content" data-target="content">
            <slot></slot>
        </div>
    </div>`;

  connectedCallback() {
    super.connectedCallback();
    this.#setStyles();
  }

  #setStyles() {
    if (this.hasLines) {
      this.style.setProperty(
        CssVariables.LINES,
        // setProperty expects a string argument
        this.lines.toString(),
      );
    }
    // Inherit font-size, need to have it as a variable to calc max-height
    this.style.setProperty(
      CssVariables.FONT_SIZE,
      this.getStringCssValue("font-size"),
    );
    // Inherit line height, need to have it as a variable to calc max-height
    const lineHeight = this.getStringCssValue("line-height");
    // If it's "normal", fall back to 1.4 as can't multiply by "normal"
    // and there is no way to get an actual number from the browser
    this.style.setProperty(
      CssVariables.LINE_HEIGHT,
      lineHeight === "normal" ? 1.4 : lineHeight,
    );
  }

  toggle() {
    if (this.controlled) {
      return;
    }
    this.dataset.expanded === "true" ? this.close() : this.open();
  }

  async open() {
    // tl;dr: line clamp is not an animatable property, so wrap the content
    // in a container and animate the height of the container instead.
    this.contentTarget.style.setProperty("-webkit-line-clamp", "unset");
    const maxHeight = `${this.contentTarget.scrollHeight}px`;
    if (!prefersReducedMotion()) {
      const animation = this.containerTarget.animate([{ maxHeight }], {
        duration: AnimationDuration.MEDIUM,
        easing: "ease-out",
      });
      await animation.finished;
    }
    this.containerTarget.style.maxHeight = maxHeight;
    this.dataset.expanded = "true";
  }

  async close() {
    if (!prefersReducedMotion()) {
      this.containerTarget.style.setProperty("scrollbar-width", "none");
      this.contentTarget.style.overflowY = "hidden";
      const maxHeight = this.getStringCssValue(CssVariables.MAX_HEIGHT);
      const animation = this.containerTarget.animate([{ maxHeight }], {
        duration: AnimationDuration.SHORT,
        easing: "ease-out",
      });
      await animation.finished;
      this.containerTarget.style.setProperty("scrollbar-width", "");
    }
    this.containerTarget.style.maxHeight = "";
    this.contentTarget.style.setProperty("-webkit-line-clamp", "");
    this.dataset.expanded = "false";
  }
}

defineElement("dt-expandabletext", DickTokExpandableText);
